import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Bold, Italic, List, ListOrdered, RemoveFormatting, Underline,
  Strikethrough, Undo2, Redo2, Quote, Smile,
} from 'lucide-react';
import {
  normalizeRichTextValue,
  richTextToPlainText,
  truncateRichText,
} from '../../utils/richText';
import './RichText.css';

const TOOLBAR_COMMANDS = [
  { command: 'bold', label: 'Negrito', Icon: Bold },
  { command: 'italic', label: 'Itálico', Icon: Italic },
  { command: 'underline', label: 'Sublinhado', Icon: Underline },
  { command: 'strikeThrough', label: 'Rasurado', Icon: Strikethrough },
  { command: 'insertUnorderedList', label: 'Lista com marcadores', Icon: List },
  { command: 'insertOrderedList', label: 'Lista numerada', Icon: ListOrdered },
];

const HISTORY_COMMANDS = [
  { command: 'undo', label: 'Desfazer', Icon: Undo2 },
  { command: 'redo', label: 'Refazer', Icon: Redo2 },
];

const BLOCK_COMMANDS = [
  { block: 'blockquote', label: 'Citação', Icon: Quote },
];

const EMOJI_OPTIONS = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
  '😍', '🤩', '😘', '😎', '🤗', '🤔', '😮', '😢',
  '😡', '😴', '🥳', '🙏', '👏', '👍', '👎', '❤️',
  '💙', '💚', '💛', '🎉', '✈️', '🌍', '🗺️', '🏖️',
  '🏔️', '🌊', '📸', '🍴', '🏨', '🚗',
];

const RichTextEditor = ({
  id,
  value = '',
  onChange,
  maxLength = 5000,
  placeholder = 'Escreve a descrição da viagem...',
  className = '',
  disabled = false,
  ariaInvalid = false,
  ariaDescribedBy,
}) => {
  const editorRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [activeCommands, setActiveCommands] = useState({});
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiPickerStyle, setEmojiPickerStyle] = useState(null);

  useEffect(() => {
    if (!emojiOpen) return undefined;
    const closePicker = (event) => {
      if (!emojiPickerRef.current?.contains(event.target)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener('mousedown', closePicker);
    return () => document.removeEventListener('mousedown', closePicker);
  }, [emojiOpen]);

  // Keep the emoji palette inside the visual viewport. This matters on
  // mobile where the keyboard reduces the viewport and an absolutely
  // positioned palette can push the wizard sideways or cover the editor.
  const positionEmojiPicker = useCallback(() => {
    const anchor = emojiPickerRef.current?.querySelector('button');
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const width = Math.min(288, Math.max(220, viewportWidth - 24));
    const estimatedHeight = Math.min(230, Math.max(150, viewportHeight - 24));
    const left = Math.max(12, Math.min(rect.left, viewportWidth - width - 12));
    const belowTop = rect.bottom + 8;
    const top = belowTop + estimatedHeight <= viewportHeight - 12
      ? belowTop
      : Math.max(12, rect.top - estimatedHeight - 8);

    setEmojiPickerStyle({ left, top, width });
  }, []);

  useEffect(() => {
    if (!emojiOpen) {
      setEmojiPickerStyle(null);
      return undefined;
    }

    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const frame = requestFrame(positionEmojiPicker);
    const visualViewport = window.visualViewport;
    window.addEventListener('resize', positionEmojiPicker);
    window.addEventListener('scroll', positionEmojiPicker, true);
    visualViewport?.addEventListener('resize', positionEmojiPicker);
    visualViewport?.addEventListener('scroll', positionEmojiPicker);

    return () => {
      cancelFrame(frame);
      window.removeEventListener('resize', positionEmojiPicker);
      window.removeEventListener('scroll', positionEmojiPicker, true);
      visualViewport?.removeEventListener('resize', positionEmojiPicker);
      visualViewport?.removeEventListener('scroll', positionEmojiPicker);
    };
  }, [emojiOpen, positionEmojiPicker]);

  const captureSelection = useCallback(() => {
    if (!editorRef.current || typeof window === 'undefined') return;
    const selection = window.getSelection?.();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (!editorRef.current || typeof window === 'undefined') return;
    editorRef.current.focus({ preventScroll: true });
    const range = savedSelectionRef.current;
    const selection = window.getSelection?.();
    if (!range || !selection || !editorRef.current.contains(range.commonAncestorContainer)) return;
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const preserveEditorSelection = (event) => {
    // Preventing the toolbar button from taking focus keeps the selected text
    // selected on desktop and on touch browsers. We also restore the cloned
    // range in the command handlers for browsers that blur contentEditable.
    event.preventDefault();
    captureSelection();
  };

  const updateCommandState = useCallback(() => {
    if (typeof document === 'undefined') return;
    const next = {};
    TOOLBAR_COMMANDS.forEach(({ command }) => {
      try {
        next[command] = document.queryCommandState(command);
      } catch {
        next[command] = false;
      }
    });
    setActiveCommands(next);
  }, []);

  useEffect(() => {
    const next = normalizeRichTextValue(value);
    if (editorRef.current && editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const raw = editorRef.current.innerHTML || '';
    const normalized = normalizeRichTextValue(raw);
    const next = richTextToPlainText(normalized).length > 0
      ? truncateRichText(normalized, maxLength)
      : '';

    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
    onChange(next);
    updateCommandState();
  }, [maxLength, onChange, updateCommandState]);

  const runCommand = (command) => {
    if (disabled || typeof document === 'undefined' || !editorRef.current) return;
    restoreSelection();
    document.execCommand(command, false, null);
    handleInput();
  };

  const runBlockCommand = (block) => {
    if (disabled || typeof document === 'undefined' || !editorRef.current) return;
    restoreSelection();
    document.execCommand('formatBlock', false, block);
    handleInput();
  };

  const insertEmoji = (emoji) => {
    if (disabled || typeof document === 'undefined' || !editorRef.current) return;
    restoreSelection();
    document.execCommand('insertText', false, emoji);
    handleInput();
    setEmojiOpen(false);
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    if (typeof document !== 'undefined') {
      document.execCommand('insertText', false, text);
    }
    handleInput();
  };

  return (
    <div className={`gm-rich-editor ${ariaInvalid ? 'has-error' : ''} ${className}`.trim()}>
      <div className="gm-rich-editor__toolbar" role="toolbar" aria-label="Formatação da descrição">
        {TOOLBAR_COMMANDS.map(({ command, label, Icon }) => (
          <button
            key={command}
            type="button"
            className={`gm-rich-editor__tool ${activeCommands[command] ? 'is-active' : ''}`}
            aria-label={label}
            aria-pressed={Boolean(activeCommands[command])}
            title={label}
            disabled={disabled}
            onPointerDown={preserveEditorSelection}
            onMouseDown={preserveEditorSelection}
            onClick={() => runCommand(command)}
          >
            <Icon size={17} strokeWidth={2} />
          </button>
        ))}
        <span className="gm-rich-editor__separator" aria-hidden="true" />
        {BLOCK_COMMANDS.map(({ block, label, Icon }) => (
          <button
            key={block}
            type="button"
            className="gm-rich-editor__tool"
            aria-label={label}
            title={label}
            disabled={disabled}
            onPointerDown={preserveEditorSelection}
            onMouseDown={preserveEditorSelection}
            onClick={() => runBlockCommand(block)}
          >
            <Icon size={17} strokeWidth={2} />
          </button>
        ))}
        <span ref={emojiPickerRef} className="gm-rich-editor__emoji">
          <button
            type="button"
            className="gm-rich-editor__tool"
            aria-label="Inserir emoji"
            title="Inserir emoji"
            aria-haspopup="dialog"
            aria-expanded={emojiOpen}
            disabled={disabled}
            onPointerDown={preserveEditorSelection}
            onMouseDown={preserveEditorSelection}
            onClick={() => setEmojiOpen((open) => !open)}
          >
            <Smile size={17} strokeWidth={2} />
          </button>
          {emojiOpen && (
            <div
              className="gm-rich-editor__emoji-picker"
              role="dialog"
              aria-label="Escolher emoji"
              style={emojiPickerStyle || undefined}
            >
              {EMOJI_OPTIONS.map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  type="button"
                  className="gm-rich-editor__emoji-item"
                  aria-label={`Inserir ${emoji}`}
                  title={`Inserir ${emoji}`}
                  onPointerDown={preserveEditorSelection}
                  onMouseDown={preserveEditorSelection}
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </span>
        <span className="gm-rich-editor__separator" aria-hidden="true" />
        {HISTORY_COMMANDS.map(({ command, label, Icon }) => (
          <button
            key={command}
            type="button"
            className="gm-rich-editor__tool"
            aria-label={label}
            title={label}
            disabled={disabled}
            onPointerDown={preserveEditorSelection}
            onMouseDown={preserveEditorSelection}
            onClick={() => runCommand(command)}
          >
            <Icon size={17} strokeWidth={2} />
          </button>
        ))}
        <span className="gm-rich-editor__separator" aria-hidden="true" />
        <button
          type="button"
          className="gm-rich-editor__tool"
          aria-label="Remover formatação"
          title="Remover formatação"
          disabled={disabled}
          onPointerDown={preserveEditorSelection}
          onMouseDown={preserveEditorSelection}
          onClick={() => runCommand('removeFormat')}
        >
          <RemoveFormatting size={17} strokeWidth={2} />
        </button>
      </div>
      <div
        id={id}
        ref={editorRef}
        className="gm-rich-editor__content gm-rich-text"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-required="true"
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        data-placeholder={placeholder}
        spellCheck="true"
        onInput={handleInput}
        // Some mobile keyboards commit the final composition while the
        // editor is losing focus. Flush once more on blur so the parent
        // wizard has the latest text before its submit button is clicked.
        onBlur={handleInput}
        onFocus={updateCommandState}
        onKeyUp={updateCommandState}
        onMouseUp={updateCommandState}
        onPaste={handlePaste}
      />
    </div>
  );
};

RichTextEditor.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  ariaInvalid: PropTypes.bool,
  ariaDescribedBy: PropTypes.string,
};

export default RichTextEditor;
